// Type definitions for child-contact relationships

export enum RelationshipType {
  PARENT = "PARENT",
  GUARDIAN = "GUARDIAN",
  GRANDPARENT = "GRANDPARENT",
  SIBLING = "SIBLING",
  AUNT_UNCLE = "AUNT_UNCLE",
  STEP_PARENT = "STEP_PARENT",
  FOSTER_PARENT = "FOSTER_PARENT",
  FAMILY_FRIEND = "FAMILY_FRIEND",
  NANNY = "NANNY",
  OTHER = "OTHER",
  AUPAIR = "AUPAIR",
  BABYSITTER = "BABYSITTER",
  COUSIN = "COUSIN",
  GODMOTHER_GODFATHER = "GODMOTHER_GODFATHER",
  GREAT_AUNT_UNCLE = "GREAT_AUNT_UNCLE",
  PEDIATRICIAN = "PEDIATRICIAN",
  NEIGHBOR = "NEIGHBOR"
}

export enum PermissionType {
  EMERGENCY_CONTACT = "EMERGENCY_CONTACT",
  PICKUP_ALLOWED = "PICKUP_ALLOWED",
  MEDICAL_DECISIONS = "MEDICAL_DECISIONS",
  INFORMATION_ACCESS = "INFORMATION_ACCESS"
}

export enum EntityStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE"
}

// Request types
export interface CreateChildContactRelationshipRequest {
  contactId: string;
  relationshipType: RelationshipType;
  permissions?: PermissionType[];
  validFrom?: string;
  validTo?: string;
  /** Custody (Sorgerecht); omitted records nothing (unknown). */
  hasCustody?: boolean;
}

export interface UpdateChildContactRelationshipRequest {
  relationshipType?: RelationshipType;
  permissions?: PermissionType[];
  status?: EntityStatus;
  validFrom?: string;
  validTo?: string;
  /** Omitted keeps the stored value. */
  hasCustody?: boolean;
}

export interface UpdatePermissionsRequest {
  permissions: PermissionType[];
}

// Response types
export interface ChildContactRelationshipResponse {
  id: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  status: EntityStatus;
  childId: string;
  contactId: string;
  relationshipType: RelationshipType;
  permissions: PermissionType[];
  validFrom?: string;
  validTo?: string;
  /** Custody (Sorgerecht): null when unknown, and always null for a parent. */
  hasCustody?: boolean | null;
}

export interface ChildContactRelationshipWithDetailsResponse extends ChildContactRelationshipResponse {
  childFirstname: string;
  childLastname: string;
  childInternalId: string;
  contactFirstname: string;
  contactLastname: string;
  contactEmail?: string;
  contactPhone?: string;
  contactPhoneCountryCode?: string;
}

export interface ListRelationshipsResponse {
  relationships: ChildContactRelationshipWithDetailsResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChildContactRelationshipAuditResponse {
  auditId: string;
  operation: string;
  auditTimestamp: string;
  auditUserId?: string;
  changedByName: string;
  changedFields?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  oldValues?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  newValues?: any;
  id: string;
  status: EntityStatus;
  childId: string;
  contactId: string;
  relationshipType: RelationshipType;
  permissions: PermissionType[];
  validFrom?: string;
  validTo?: string;
  hasCustody?: boolean | null;
}

export interface AuditHistoryResponse {
  records: ChildContactRelationshipAuditResponse[];
  total: number;
  limit: number;
  offset: number;
}

// Helper type for UI display
export interface RelationshipTypeOption {
  value: RelationshipType;
  label: string;
  icon?: string;
}

export interface PermissionTypeOption {
  value: PermissionType;
  label: string;
  description?: string;
  icon?: string;
}